import { some, filter } from 'lodash-es'
import { object, string, array, number, boolean } from 'yup'
import { AssemblyTypeEnum } from 'src/constants/assemblies'

const vibrationSettingsSchema = object({
  requirement: string().required(),
  screed_data: object({ screed: string(), fill: string() }),
  screed_height: number().positive(),
  screed_e_modul: number().positive(),
})

const isTimberFrameAssemblyKind = (kind: AssemblyKind) =>
  kind === 'timber-frame-assembly' ||
  kind === 'timber-frame-assembly-wall' ||
  kind === 'timber-frame-assembly-roof' ||
  kind === 'timber-frame-assembly-ceiling'

const isTimberFrameWallAssembly = (kind: AssemblyKind, usageType: AssemblyTypes) =>
  kind === 'timber-frame-assembly-wall' ||
  (kind === 'timber-frame-assembly' &&
    (usageType === AssemblyTypeEnum.Inner || usageType === AssemblyTypeEnum.Outer))

const isTimberFrameRoofAssembly = (kind: AssemblyKind, usageType: AssemblyTypes) =>
  kind === 'timber-frame-assembly-roof' ||
  (kind === 'timber-frame-assembly' && usageType === AssemblyTypeEnum.Roof)

const isTimberFrameCeilingAssembly = (kind: AssemblyKind, usageType: AssemblyTypes) =>
  kind === 'timber-frame-assembly-ceiling' ||
  (kind === 'timber-frame-assembly' && usageType === AssemblyTypeEnum.Ceiling)

const isCLTAssemblyKind = (kind: AssemblyKind) =>
  kind === 'clt-assembly' || kind === 'clt-assembly-roof' || kind === 'clt-assembly-ceiling'

const isCLTRoofAssembly = (kind: AssemblyKind, usageType: AssemblyTypes) =>
  kind === 'clt-assembly-roof' || (kind === 'clt-assembly' && usageType === AssemblyTypeEnum.Roof)

const isCLTCeilingAssembly = (kind: AssemblyKind, usageType: AssemblyTypes) =>
  kind === 'clt-assembly-ceiling' ||
  (kind === 'clt-assembly' && usageType === AssemblyTypeEnum.Ceiling)

const hasLayerOfKind = (layers: unknown[], kinds: LayerType[]) =>
  some(layers, layer => layer && kinds.includes((layer as Layer).kind))

export const assemblySchema = object({
  guid: string().default(''),
  name: string().required().default(''),
  usage_type: string().required(),
  isStandard: boolean().required(),
  short_name: string()
    .default('')
    .test('required-short-name', 'Kurzbezeichnung ist erforderlich', value => !!value?.trim())
    .test('maxLength', function (value) {
      const { path, createError } = this
      return (
        value.length <= 25 ||
        createError({
          path,
          message: `Maximal 25 Zeichen. ${value.length} Zeichen wurden eingegeben`,
        })
      )
    }),
  description: string().default(''),
  kind: string().required().default('timber-frame-assembly-wall'),
  rips_held_by_plate: boolean().default(true),
  vibration_check_settings: vibrationSettingsSchema
    .default(null)
    .nullable()
    .test({
      name: 'ceiling-vibration-required',
      message: 'Schwingungsnachweis muss für Deckenaufbauten angegeben werden',
      test: function (value: VibrationCheckSettings | null | undefined) {
        const { kind, usage_type } = this.parent as Assembly
        const requiresVibration = [
          isTimberFrameCeilingAssembly(kind, usage_type),
          isCLTCeilingAssembly(kind, usage_type),
        ].some(Boolean)
        if (requiresVibration) {
          return value !== null
        }

        const disallowVibration = [
          isTimberFrameRoofAssembly(kind, usage_type),
          isCLTRoofAssembly(kind, usage_type),
        ].some(Boolean)
        if (disallowVibration) {
          return value === null
        }

        return true
      },
    }),
  layers: array()
    .min(1, 'Fügen Sie mindestens eine Schicht hinzu')
    .test({
      name: 'min-timber-frame-test',
      message: 'Holzaufbauten müssen mindestens eine Holzrahmenschicht haben',
      test: function (layers) {
        const { kind, usage_type } = this.parent as Assembly
        if (!isTimberFrameAssemblyKind(kind)) return true

        const isRoofOrCeilingAssembly =
          isTimberFrameRoofAssembly(kind, usage_type) ||
          isTimberFrameCeilingAssembly(kind, usage_type)

        const targetKinds = isRoofOrCeilingAssembly
          ? (['timber-frame-layer-roof-ceiling'] as LayerType[])
          : (['timber-frame-layer-wall', 'timber-frame-layer'] as LayerType[])

        return hasLayerOfKind(Array.isArray(layers) ? layers : [], targetKinds)
      },
    })
    .min(1, 'Fügen Sie mindestens eine Schicht hinzu')
    .test({
      name: 'min-clt-frame-test',
      message: 'CLT Aufbauten müssen mindestens eine CLT Schicht haben',
      test: function (layers) {
        const { kind } = this.parent as Assembly
        if (!isCLTAssemblyKind(kind)) return true

        return some(layers, ['kind', 'clt-assembly-layer'])
      },
    })
    .test({
      name: 'min-plate-layer-test',
      message: 'Wandaufbauten müssen mindestens eine Plattenschicht haben',
      test: function (layers) {
        const { kind, usage_type } = this.parent as Assembly
        if (!isTimberFrameWallAssembly(kind, usage_type)) return true

        return hasLayerOfKind(Array.isArray(layers) ? layers : [], [
          'timber-frame-layer-wall',
          'timber-frame-layer',
        ])
      },
    })
    .test({
      name: 'max-timber-frame-test',
      message: 'Holzaufbauten können maximal eine Holzrahmenschicht enthalten',
      test: function (layers) {
        const { kind, usage_type } = this.parent as Assembly
        if (!isTimberFrameAssemblyKind(kind)) return true

        const isRoofOrCeilingAssembly =
          isTimberFrameRoofAssembly(kind, usage_type) ||
          isTimberFrameCeilingAssembly(kind, usage_type)

        const targetKinds = isRoofOrCeilingAssembly
          ? ['timber-frame-layer-roof-ceiling']
          : ['timber-frame-layer-wall', 'timber-frame-layer']

        const filteredLayers = filter(Array.isArray(layers) ? layers : [], layer =>
          targetKinds.includes((layer as Layer).kind),
        )

        return filteredLayers.length <= 1
      },
    })
    .test({
      name: 'max-clt-frame-test',
      message: 'CLT Aufbauten können maximal eine CLT Schicht enthalten',
      test: function (layers) {
        const { kind } = this.parent as Assembly
        if (!isCLTAssemblyKind(kind)) return true

        return filter(layers, ['kind', 'clt-assembly-layer']).length <= 1
      },
    })
    .test({
      name: 'max-plate-layer-beside-timber-or-clt-frame-test',
      message:
        'Nur eine statisch wirksame Platte pro Seite ist erlaubt. Nutze stattdessen eine Produktschicht.',
      test: function (layers = []) {
        let count = 0

        ;(layers as Layer[]).every(layer => {
          if (count > 1) return false
          // reset count if left of timber frame passes condition
          if (
            layer.kind === 'timber-frame-layer' ||
            layer.kind === 'timber-frame-layer-wall' ||
            layer.kind === 'timber-frame-layer-roof-ceiling' ||
            layer.kind === 'clt-assembly-layer'
          )
            count = 0
          if (layer.kind === 'plate-layer') count++
          return true
        })

        return count <= 1
      },
    })
    .default([]),
})
